/**
 * Auth Token Store
 *
 * Module-level token store that can be updated from React context
 * and read by the GraphQL client (which is not a React component).
 *
 * Eagerly initializes from localStorage so the token is available before
 * React effects fire — but only if the token is not expired, to avoid
 * flooding the server with 401s that trigger a cascading renewal loop.
 */

import { getOidcStorageKey } from './config';

// Derived from the same authority/client_id as the OIDC provider so the eager
// read stays in sync when the issuer changes during the Pharos cutover (CDEV-376).
const OIDC_STORAGE_KEY = getOidcStorageKey();

function initTokenFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(OIDC_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    if (!stored?.access_token) return null;
    // Only use the stored token if it's not yet expired.
    // Sending an expired token causes 401s → auth:unauthorized events →
    // signinSilent() calls, all before React even mounts. Better to wait
    // for AuthTokenSync to set the token after OIDC renewal completes.
    if (stored.expires_at && stored.expires_at < Date.now() / 1000) {
      return null;
    }
    return stored.access_token;
  } catch {
    // Corrupt or missing storage — fall through
  }
  return null;
}

let accessToken: string | null = initTokenFromStorage();
let tokenExpiresAt: number | null = null;

/**
 * Set the current access token
 * Called from AuthTokenSync component when auth state changes
 */
export function setAccessToken(token: string | null, expiresAt?: number): void {
  accessToken = token;
  tokenExpiresAt = expiresAt ?? null;
}

/**
 * Get the current access token
 * Used by GraphQL client to add Authorization header
 * Returns null if the token has expired, preventing stale tokens from being sent.
 */
export function getAccessToken(): string | null {
  if (!accessToken) return null;
  if (tokenExpiresAt && tokenExpiresAt < Date.now() / 1000) {
    accessToken = null;
    tokenExpiresAt = null;
    return null;
  }
  return accessToken;
}

/**
 * Check if user is authenticated (has a token)
 */
export function isAuthenticated(): boolean {
  return accessToken !== null;
}

/**
 * Clear the access token (on logout)
 */
export function clearAccessToken(): void {
  accessToken = null;
  tokenExpiresAt = null;
}
